import { IHostCallbacks } from './iclientruntime';
import { IAuthTokenEntry, IAuthTokenRequest } from '@augloop/schemas/dist/src/client/schemas';
import { Utils } from './utils';

interface ITokenRequestRecord {
    requestPromise: Promise<void>;
    requestTime: number;
}

export class TokenCache {
    // Expiration of tokens in the cache in ms
    private readonly tokenExpirationMs: number;

    // Map from token request key to token
    private readonly tokenCache: Map<string, string> = new Map();

    // Map from token request key to token request (for compatibility with Maps in ChakraCore)
    private readonly requestMap: Map<string, IAuthTokenRequest> = new Map();

    // Map from token request key to token request record (for tracking pending requests)
    private readonly requestRecordMap: Map<string, ITokenRequestRecord> = new Map();

    public constructor(tokenExpirationMs: number = 55 * 60 * 1000 /* 55 minutes */) {
        this.tokenExpirationMs = tokenExpirationMs;
    }

    /**
     * Request a token from the host for the given tokenRequest if we have no token or the token expired
     * and update the token cache accordingly. Does not instantaneously update the token but subsequent
     * requests will resolve the same promise.
     */
    public updateAuthToken(hostCallbacks: IHostCallbacks, tokenRequest: IAuthTokenRequest): Promise<void> {
        const requestKey: string = JSON.stringify(tokenRequest);
        let tokenRequestRecord: ITokenRequestRecord = this.requestRecordMap.get(requestKey);
        if (tokenRequestRecord === undefined || this.isTokenExpired(tokenRequestRecord)) {
            tokenRequestRecord = {
                requestPromise: hostCallbacks
                    .requestAuthToken(tokenRequest)
                    .then(response => {
                        if (!response || !response.Token) {
                            throw new Error(`No token available for request ${requestKey}`);
                        }

                        this.tokenCache.set(requestKey, response.Token);
                    }),
                requestTime: Utils.getCurrentTimeMs()
            };
            this.requestRecordMap.set(requestKey, tokenRequestRecord);
        }

        return tokenRequestRecord.requestPromise;
    }

    /**
     * Get all tokens (and tickets) in the cache and update expired tokens
     */
    public getAuthTokenEntries(hostCallbacks: IHostCallbacks, docId?: string): IAuthTokenEntry[] {
        return Array.from(this.tokenCache.keys())
            .map(requestKey => {
                // Get the auth token request or parse it from the key
                let request: IAuthTokenRequest = this.requestMap.get(requestKey);
                if (request === undefined) {
                    request = JSON.parse(requestKey);
                    this.requestMap.set(requestKey, request);
                }

                return { requestKey: requestKey, request: request };
            })
            .filter(({ request }) => {
                // Get requests matching the given docId
                return docId ? request.DocId === docId : true;
            })
            .map(({ requestKey, request }) => {
                if (this.isTokenExpired(this.requestRecordMap.get(requestKey))) {
                    // NOTE: Does not update the token immediately for this request
                    this.updateAuthToken(hostCallbacks, request);
                }

                return {
                    ticket: request.Tickets[0], // NOTE: We will need to choose the correct ticket when we support multiple identities
                    token: this.tokenCache.get(requestKey)
                };
            });
    }

    private isTokenExpired(tokenRequestRecord: ITokenRequestRecord): boolean {
        return Utils.getCurrentTimeMs() > tokenRequestRecord.requestTime + this.tokenExpirationMs;
    }
}
